import { Injectable, NestMiddleware } from '@nestjs/common';
import { NextFunction, Request, Response } from 'express';
import { PrismaService } from '@cart/api-data-access';
import {
  DEFAULT_ORGANIZATION_ID,
  DEFAULT_STORE_ID,
  StoreContext,
  StoreContextService,
} from './store-context.service';

// Middleware, not an interceptor: guards run before interceptors in Nest's
// request lifecycle, so a store-context *interceptor* would still see the
// AsyncLocalStorage default (store_demo) inside any guard that reads
// StoreContextService — e.g. AdminAccessGuard's staff lookup — regardless of
// what the client actually sent. Middleware runs first, so the resolved
// store is in place before any guard executes.
@Injectable()
export class StoreContextMiddleware implements NestMiddleware {
  constructor(
    private readonly prisma: PrismaService,
    private readonly storeContext: StoreContextService
  ) {}

  use(request: Request, response: Response, next: NextFunction): void {
    this.resolveStore(request).then((store) => this.storeContext.run(store, next));
  }

  private async resolveStore(request: Request): Promise<StoreContext> {
    const domain = this.headerValue(request.headers['x-store-domain']) ?? this.host(request);

    try {
      const headerStoreId = this.headerValue(request.headers['x-store-id']);

      if (headerStoreId) {
        const store = await this.prisma.store.findFirst({
          where: { id: headerStoreId, isActive: true },
          select: { id: true, organizationId: true },
        });

        if (store) {
          return { storeId: store.id, organizationId: store.organizationId, resolved: true };
        }
      }

      if (domain) {
        const storeDomain = await this.prisma.storeDomain.findFirst({
          where: { domain, isActive: true },
          select: {
            domain: true,
            store: {
              select: { id: true, organizationId: true, isActive: true },
            },
          },
        });

        if (storeDomain?.store.isActive) {
          return {
            storeId: storeDomain.store.id,
            organizationId: storeDomain.store.organizationId,
            domain: storeDomain.domain,
            resolved: true,
          };
        }
      }
    } catch {
      return this.fallback(domain);
    }

    return this.fallback(domain);
  }

  private fallback(domain?: string): StoreContext {
    return {
      storeId: DEFAULT_STORE_ID,
      organizationId: DEFAULT_ORGANIZATION_ID,
      domain,
      resolved: false,
    };
  }

  private headerValue(value: string | string[] | undefined): string | undefined {
    const raw = Array.isArray(value) ? value[0] : value;
    return raw?.split(',')[0]?.trim().toLowerCase() || undefined;
  }

  private host(request: Request): string | undefined {
    const host = this.headerValue(request.headers.host);
    return host?.split(':')[0];
  }
}
